import { useEffect, useState } from 'react';
import { Lock, Paperclip, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  useFileNotes,
  useCreateFileNote,
  useUpdateFileNote,
  useDeleteFileNote,
  useUploadFileNoteAttachment,
  useDeleteFileNoteAttachment,
  fileNoteAttachmentUrl,
} from '../api.js';
import { EmployeeMultiSelect } from './EmployeeMultiSelect.js';
import { FileNoteViewModal } from './FileNoteViewModal.js';
import { Table, type Column } from '../../../shell/Table.js';
import { Button } from '../../../shell/Button.js';
import { ViewRecordButton } from '../../../shell/DetailViewModal.js';
import { Modal } from '../../../shell/Modal.js';
import { Input, Textarea, Field } from '../../../shell/Field.js';
import { Badge } from '../../../shell/Badge.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { formatDate } from '../../../lib/format.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError, openApiContent } from '../../../lib/api.js';
import { useAuthStore } from '../../../store/auth.js';
import type { EmployeeFileNote, FileNoteAttachment } from '@shared/types';

function toDateIso(day: string): string {
  if (!day) return new Date().toISOString();
  return new Date(`${day}T00:00:00.000Z`).toISOString();
}

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export function EmployeeFileNotesSection({ employeeId }: { employeeId: string }) {
  const { data, isLoading } = useFileNotes({ employeeId });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeFileNote | null>(null);
  const [viewing, setViewing] = useState<EmployeeFileNote | null>(null);
  const deleteNote = useDeleteFileNote();

  const handleDelete = async (note: EmployeeFileNote) => {
    const onMultiple = note.employeeIds.length > 1;
    const msg = onMultiple
      ? 'Delete this note from all linked employee files? This cannot be undone.'
      : 'Delete this file note? This cannot be undone.';
    if (!window.confirm(msg)) return;
    try {
      await deleteNote.mutateAsync(note.id);
      toast('success', onMultiple ? 'File note removed from all employees.' : 'File note deleted.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to delete.';
      toast('error', msg);
    }
  };

  const columns: Column<EmployeeFileNote>[] = [
    { key: 'date', header: 'Date', render: (n) => formatDate(n.date) },
    {
      key: 'title',
      header: 'Title',
      render: (n) => (
        <span className="font-medium">
          {n.title}
          {n.attachments.length > 0 && (
            <span className="ml-2 inline-flex items-center gap-0.5 align-middle text-xs font-normal text-slate-400" title={`${n.attachments.length} attachment(s)`}>
              <Paperclip className="h-3 w-3" />
              {n.attachments.length}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'details',
      header: 'Preview',
      render: (n) => <span className="line-clamp-1 text-slate-600 dark:text-slate-400">{n.details}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (n) => (
        <div className="flex justify-end gap-1">
          <ViewRecordButton label={n.title} onClick={() => setViewing(n)} />
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Edit ${n.title}`}
            onClick={(e) => {
              e.stopPropagation();
              setEditing(n);
              setModalOpen(true);
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Delete ${n.title}`}
            loading={deleteNote.isPending}
            onClick={(e) => {
              e.stopPropagation();
              void handleDelete(n);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Personnel File Notes
          </h2>
          <Badge tone="red">Admin only</Badge>
          {data?.items.length !== undefined && (
            <span className="text-xs text-slate-400">({data.items.length})</span>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4" /> Add Note
        </Button>
      </div>
      <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
        Documented items on employee files. Not visible to employees. Notes can be linked to multiple
        employees (e.g. disputes).
      </p>

      {isLoading ? (
        <p className="py-4 text-center text-sm text-slate-400">Loading…</p>
      ) : data && data.items.length > 0 ? (
        <Table columns={columns} data={data.items} />
      ) : (
        <EmptyState
          title="No file notes"
          description="Add an admin-only note to document something on this employee's personnel file."
        />
      )}

      <FileNoteModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        defaultEmployeeId={employeeId}
        note={editing}
      />

      <FileNoteViewModal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        note={viewing}
      />
    </div>
  );
}

/** Renders saved attachments as thumbnails (images) or file chips, with optional remove. */
export function FileNoteAttachments({
  noteId,
  attachments,
  onRemove,
}: {
  noteId: string;
  attachments: FileNoteAttachment[];
  onRemove?: (attachment: FileNoteAttachment) => void;
}) {
  if (attachments.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {attachments.map((att) => {
        const url = fileNoteAttachmentUrl(noteId, att.id);
        const isImage = att.contentType.startsWith('image/');
        return (
          <div
            key={att.id}
            className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800"
          >
            {isImage ? (
              <img src={url} alt={att.fileName} loading="lazy" className="h-8 w-8 rounded object-cover" />
            ) : (
              <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />
            )}
            <a
              href={url}
              onClick={(event) => {
                event.preventDefault();
                void openApiContent(url).catch((error: unknown) => {
                  toast('error', error instanceof Error ? error.message : 'Failed to open attachment.');
                });
              }}
              className="max-w-[12rem] truncate text-slate-700 hover:underline dark:text-slate-200"
              title={att.fileName}
            >
              {att.fileName}
            </a>
            {onRemove && (
              <button
                type="button"
                aria-label={`Remove ${att.fileName}`}
                onClick={() => onRemove(att)}
                className="shrink-0 text-slate-400 hover:text-red-500"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FileNoteModal({
  open,
  onClose,
  defaultEmployeeId,
  note,
}: {
  open: boolean;
  onClose: () => void;
  defaultEmployeeId: string;
  note?: EmployeeFileNote | null;
}) {
  const create = useCreateFileNote();
  const update = useUpdateFileNote();
  const uploadAttachment = useUploadFileNoteAttachment();
  const deleteAttachment = useDeleteFileNoteAttachment();
  const { user } = useAuthStore();
  const isEdit = Boolean(note);

  const [employeeIds, setEmployeeIds] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [date, setDate] = useState('');
  const [attachments, setAttachments] = useState<FileNoteAttachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  useEffect(() => {
    if (!open) return;
    if (note) {
      setEmployeeIds(note.employeeIds);
      setTitle(note.title);
      setDetails(note.details);
      setDate(toDateInput(note.date));
      setAttachments(note.attachments);
    } else {
      setEmployeeIds([defaultEmployeeId]);
      setTitle('');
      setDetails('');
      setDate(toDateInput(new Date().toISOString()));
      setAttachments([]);
    }
    setPendingFiles([]);
  }, [open, note, defaultEmployeeId]);

  const handleClose = () => {
    setEmployeeIds([]);
    setTitle('');
    setDetails('');
    setDate('');
    setAttachments([]);
    setPendingFiles([]);
    onClose();
  };

  const handleAddFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (isEdit && note) {
      for (const file of Array.from(files)) {
        try {
          const att = await uploadAttachment.mutateAsync({ noteId: note.id, file });
          setAttachments((prev) => [...prev, att]);
        } catch (err) {
          const msg = err instanceof ApiRequestError ? err.body.message : `Failed to upload ${file.name}.`;
          toast('error', msg);
        }
      }
    } else {
      setPendingFiles((prev) => [...prev, ...Array.from(files)]);
    }
  };

  const handleRemoveAttachment = async (att: FileNoteAttachment) => {
    if (!note) return;
    try {
      await deleteAttachment.mutateAsync({ noteId: note.id, attachmentId: att.id });
      setAttachments((prev) => prev.filter((a) => a.id !== att.id));
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to remove attachment.';
      toast('error', msg);
    }
  };

  const handleSubmit = async () => {
    try {
      if (isEdit && note) {
        await update.mutateAsync({
          id: note.id,
          body: { title, details, date: toDateIso(date), employeeIds },
        });
        toast('success', 'File note updated.');
      } else {
        const created = await create.mutateAsync({
          employeeIds,
          title,
          details,
          date: toDateIso(date),
        });
        // Attach any pending files to the newly created note.
        for (const file of pendingFiles) {
          try {
            await uploadAttachment.mutateAsync({ noteId: created.id, file });
          } catch (err) {
            const msg = err instanceof ApiRequestError ? err.body.message : `Failed to upload ${file.name}.`;
            toast('error', msg);
          }
        }
        toast('success', employeeIds.length > 1
          ? `File note added to ${employeeIds.length} employees.`
          : 'File note added.');
      }
      handleClose();
    } catch (err) {
      const msg = err instanceof ApiRequestError
        ? err.body.message
        : (isEdit ? 'Failed to update.' : 'Failed to save.');
      toast('error', msg);
    }
  };

  const pending = create.isPending || update.isPending || uploadAttachment.isPending;
  const canSubmit = Boolean(title.trim() && details.trim() && date && employeeIds.length > 0);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isEdit ? 'Edit File Note' : 'Add Personnel File Note'}
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          This note is stored on personnel files and will not be visible to the employees listed.
        </p>
        <EmployeeMultiSelect
          value={employeeIds}
          onChange={setEmployeeIds}
          pinnedId={isEdit ? undefined : defaultEmployeeId}
          excludeIds={user?.employeeId ? [user.employeeId] : []}
        />
        <Field label="Date">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Title">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Workplace dispute — mediation summary"
          />
        </Field>
        <Textarea
          label="Details"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={5}
          placeholder="Document what was discussed, observed, or decided…"
        />
        <Field label="Attachments (photos & documents)">
          {isEdit && note && attachments.length > 0 && (
            <div className="mb-2">
              <FileNoteAttachments
                noteId={note.id}
                attachments={attachments}
                onRemove={handleRemoveAttachment}
              />
            </div>
          )}
          {!isEdit && pendingFiles.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {pendingFiles.map((file, idx) => (
                <div
                  key={`${file.name}-${idx}`}
                  className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800"
                >
                  <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="max-w-[12rem] truncate text-slate-700 dark:text-slate-200" title={file.name}>
                    {file.name}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
                    className="shrink-0 text-slate-400 hover:text-red-500"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <input
            type="file"
            multiple
            onChange={(e) => {
              void handleAddFiles(e.target.files);
              e.target.value = '';
            }}
            className="block w-full text-sm text-slate-500 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200 dark:text-slate-400 dark:file:bg-slate-700 dark:file:text-slate-200"
          />
          {!isEdit && (
            <p className="mt-1 text-xs text-slate-400">Files are attached once the note is saved.</p>
          )}
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={pending} disabled={!canSubmit}>
            {isEdit ? 'Save Changes' : 'Save Note'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
